/**
 * Layout Component
 * ================
 * Main layout wrapper for Invoice Checker pages.
 * Provides sidebar navigation and content area.
 */

import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Sidebar, { NavItem } from '../../common/Sidebar';
import { useAuth } from '../../../context/AuthContext';
import { config } from '../../../utils/config';
import './Layout.scss';

interface LayoutProps {
    children: React.ReactNode;
    pageTitle: string;
    pageDescription?: string;
    headerActions?: React.ReactNode;
}

// Navigation icons as SVG components
const DashboardIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1" />
        <rect x="14" y="3" width="7" height="7" rx="1" />
        <rect x="3" y="14" width="7" height="7" rx="1" />
        <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
);

const RFQIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
    </svg>
);

const UploadIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
        <polyline points="17 8 12 3 7 8" />
        <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
);

const SettingsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </svg>
);

const ReconciliationIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <path d="M9 15l2 2 4-4" />
    </svg>
);

const QuotesIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
        <rect x="9" y="3" width="6" height="4" rx="1" />
        <path d="M9 12h6" />
        <path d="M9 16h6" />
    </svg>
);

const InvoiceDetailsIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="12" y1="18" x2="12" y2="12" />
        <line x1="9" y1="15" x2="15" y2="15" />
    </svg>
);

const Layout: React.FC<LayoutProps> = ({
    children,
    pageTitle,
    pageDescription,
    headerActions
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    // Navigation items with expandable menus
    const navItems: NavItem[] = [
        {
            id: 'dashboard',
            label: 'Dashboard',
            icon: <DashboardIcon />,
            route: '/'
        },
        {
            id: 'reconciliation',
            label: 'Reconciliation',
            icon: <ReconciliationIcon />,
            children: [
                { id: 'recon-open', label: 'Open', icon: <></>, route: '/reconciliation' },
                { id: 'recon-matched', label: 'Matched', icon: <></>, route: '/reconciliation?filter=matched' }
            ]
        },
        {
            id: 'quotes-contracts',
            label: 'Quotes & Contracts',
            icon: <QuotesIcon />,
            children: [
                { id: 'quotes-open', label: 'Open', icon: <></>, route: '/quotes-contracts' },
                { id: 'quotes-matched', label: 'Matched', icon: <></>, route: '/quotes-contracts?filter=matched' }
            ]
        },
        {
            id: 'separator-1',
            label: '',
            icon: <></>,
            isSeparator: true
        },
        {
            id: 'rfq-invoices',
            label: 'RFQ Invoices',
            icon: <RFQIcon />,
            route: '/rfq-invoices'
        },
        {
            id: 'invoice-details',
            label: 'Invoice Details',
            icon: <InvoiceDetailsIcon />,
            route: '/invoice-details'
        },
        {
            id: 'bulk-upload',
            label: 'Bulk Upload',
            icon: <UploadIcon />,
            route: '/bulk-upload'
        },
        {
            id: 'separator-2',
            label: '',
            icon: <></>,
            isSeparator: true
        },
        {
            id: 'settings',
            label: 'Settings',
            icon: <SettingsIcon />,
            route: '/settings'
        }
    ];

    const handleNavigate = (route: string) => {
        navigate(route);
    };

    const handleLogout = () => {
        logout();
    };

    return (
        <div className="layout">
            <Sidebar
                logo="Invoice Checker"
                navItems={navItems}
                currentRoute={location.pathname + location.search}
                onNavigate={handleNavigate}
                user={{
                    name: user?.name || 'User',
                    email: user?.email || ''
                }}
                onLogout={handleLogout}
            />

            <main className="layout__main">
                {/* Header */}
                <header className="layout__header">
                    <div className="layout__header-left">
                        <h1 className="layout__page-title">{pageTitle}</h1>
                        {pageDescription && (
                            <p className="layout__page-description">{pageDescription}</p>
                        )}
                    </div>
                    {headerActions && (
                        <div className="layout__header-actions">
                            {headerActions}
                        </div>
                    )}
                </header>

                {/* Content */}
                <div className="layout__content">
                    {children}
                </div>
            </main>
        </div>
    );
};

export default Layout;
